// CID Copilot AI — general agent assistant (GLM tool-loop, agent-scoped).
import client, { tokens } from './client';

/**
 * POST /api/agent/assistant [AI chat]
 * Body: { messages: [{role:'user'|'assistant', content}] }.
 * Returns { reply, cards: [{title, subtitle, path}], tools_used }.
 * Longer timeout than the app default (20s): one reply can chain several
 * model + tool rounds server-side.
 */
export const askCopilot = (data = {}) => client.post(`/api/agent/assistant`, data, { timeout: 60000 });

/**
 * POST /api/agent/assistant/stream [AI chat, live]
 * Same body; responds as SSE. Calls the handlers as events arrive:
 *   onStatus(label)  — what the assistant is doing ("Searching listings…")
 *   onDelta(text)    — answer text as it generates
 *   onDone({reply, cards, consulted}) — canonical final payload
 * Throws on transport/error events so the caller can fall back to askCopilot.
 */
/** POST /api/agent/assistant/action — execute a Confirm-button write proposal. */
export const runAction = (data = {}) => client.post(`/api/agent/assistant/action`, data, { timeout: 30000 });

/** POST /api/agent/assistant/feedback — thumbs rating (1 | -1) for a reply's log_id. */
export const sendFeedback = (log_id, rating) => client.post(`/api/agent/assistant/feedback`, { log_id, rating });

export async function streamCopilot(data, { onStatus, onDelta, onDone } = {}, signal) {
  const res = await fetch(`${client.defaults.baseURL}/api/agent/assistant/stream`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tokens.access}` },
    body: JSON.stringify(data),
    signal,
  });
  if (!res.ok || !res.body) throw new Error(`Copilot stream failed (${res.status})`);
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  let done = false;
  while (!done) {
    const { value, done: end } = await reader.read();
    if (end) break;
    buf += decoder.decode(value, { stream: true });
    let nl;
    while ((nl = buf.indexOf('\n\n')) !== -1) {
      const frame = buf.slice(0, nl).trim();
      buf = buf.slice(nl + 2);
      if (!frame.startsWith('data:')) continue;
      let ev;
      try { ev = JSON.parse(frame.slice(5).trim()); } catch { continue; }
      if (ev.type === 'status') onStatus?.(ev.label);
      else if (ev.type === 'delta') onDelta?.(ev.text);
      else if (ev.type === 'done') { done = true; onDone?.(ev); }
      else if (ev.type === 'error') throw new Error(ev.message || 'Copilot unavailable');
    }
  }
  if (!done) throw new Error('Copilot stream ended unexpectedly');
}
